import React, { useRef } from 'react';
import { getCellColour } from '../../utils/treeLayout';

const MemberCell = ({ member, isCreator, leftStripColor, rightStripColor, onRightClick, onClick, onDoubleClick }) => {
  const clickCount = useRef(0);
  const clickTimer = useRef(null);
  const longPressTimer = useRef(null);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const longPressTriggered = useRef(false);

  const colours = getCellColour(member, isCreator);

  const initials = member.name
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleClick = (e) => {
    if (e.button !== 0) return;
    clickCount.current += 1;
    if (clickTimer.current) clearTimeout(clickTimer.current);

    clickTimer.current = setTimeout(() => {
      if (clickCount.current === 1) {
        onClick && onClick(member);
      } else if (clickCount.current >= 3) {
        onDoubleClick && onDoubleClick(member);
      }
      clickCount.current = 0;
    }, 300);
  };
const LONG_PRESS_DURATION = 500; // ms
  const MOVE_THRESHOLD = 10; // px

  const handleTouchStart = (e) => {
    longPressTriggered.current = false;
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };

    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      // Cancel any pending click/double-click logic
      if (clickTimer.current) clearTimeout(clickTimer.current);
      clickCount.current = 0;
      onRightClick && onRightClick(
        { preventDefault: () => {}, clientX: touch.clientX, clientY: touch.clientY },
        member
      );
    }, LONG_PRESS_DURATION);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchStartPos.current.x);
    const dy = Math.abs(touch.clientY - touchStartPos.current.y);
    if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    // If long press fired, swallow the upcoming click so it doesn't also open the panel
    if (longPressTriggered.current) {
      longPressTriggered.current = false;
    }
  };
  const isCreatorCell = member.generation === 0;

  return (
    <div
      data-member-id={member.memberId}
      style={{
        ...styles.cell,
        background: colours.bg,
        border: `${isCreatorCell ? '2.5px' : '1.5px'} solid ${colours.border}`,
        opacity: member.isDeceased ? 0.73 : 1,
        boxShadow: isCreatorCell
          ? `0 0 0 3px #D4A01730, 0 1px 4px rgba(0,0,0,0.08)`
          : '0 1px 4px rgba(0,0,0,0.08)',
        outline: 'none',
        outlineOffset: '2px',
        cursor: 'pointer',
        position: 'relative',
        zIndex: 1,           // cells sit above the SVG connector lines
      }}
      onMouseDown={(e) => handleClick(e)}
      onContextMenu={(e) => {
        e.preventDefault();
        onRightClick && onRightClick(e, member);
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      title={member.name}
    >

      {/* Deceased indicator — top left */}
      {member.isDeceased && (
        <span style={styles.deceasedIcon}>🪔</span>
      )}

      {/* Linked tree indicator — top right */}
      {member.linkedFamilyActive && (
        <span style={styles.linkedIcon}>🔗</span>
      )}

      {/* Avatar */}
      <div style={{
        ...styles.avatar,
        background: colours.border,
      }}>
        {member.profilePhotoUrl ? (
          <img src={member.profilePhotoUrl} alt={member.name} style={styles.avatarImg} />
        ) : (
          <span style={styles.initials}>{initials}</span>
        )}
      </div>

      {/* Name */}
      <div style={{ ...styles.name, color: colours.text }}>
        {member.name}
      </div>

      {/* Relation */}
      {member.relation && (
        <div style={styles.relation}>{member.relation}</div>
      )}

      {/* Bottom badges — photo left, story right */}
      <div style={styles.bottomBadges}>
        <span style={styles.photoBadge}>
          {member.profilePhotoUrl ? '📷' : ''}
        </span>
        <span style={styles.storyBadge}>
          {member.story ? '📝' : ''}
        </span>
      </div>
    </div>
  );
};

const styles = {
  cell: {
    width: '100px',
    height: '64px',
    borderRadius: '8px',
    padding: '5px 4px 3px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    boxSizing: 'border-box',
    userSelect: 'none',
    transition: 'transform 0.1s, box-shadow 0.1s',
    flexShrink: 0,
  },
  deceasedIcon: {
    position: 'absolute',
    top: '2px',
    left: '3px',
    fontSize: '8px',
    lineHeight: 1,
  },
  linkedIcon: {
    position: 'absolute',
    top: '2px',
    right: '3px',
    fontSize: '8px',
    lineHeight: 1,
  },
  avatar: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  initials: {
    fontSize: '9px',
    fontWeight: 'bold',
    color: '#ffffff',
  },
  name: {
    fontSize: '8.5px',
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: 1.1,
    width: '92px',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  relation: {
    fontSize: '7px',
    color: '#888',
    textAlign: 'center',
    width: '92px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bottomBadges: {
    position: 'absolute',
    bottom: '2px',
    left: '3px',
    right: '3px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  photoBadge: { fontSize: '7px' },
  storyBadge: { fontSize: '7px' },
};

export default MemberCell;
